import React, { useState, useEffect } from 'react';

interface SideNavIndicatorProps {
  isDark: boolean;
}

const SideNavIndicator: React.FC<SideNavIndicatorProps> = ({ isDark }) => {
  const [activeSection, setActiveSection] = useState('about');

  const sections = [
    { id: 'about', label: 'About' },
    { id: 'projects', label: 'Projects' },
    { id: 'contact', label: 'Contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200; // Small offset from top
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // If we're near the bottom of the page, activate contact
      if (windowHeight + window.scrollY >= documentHeight - 100) {
        setActiveSection('contact');
        return;
      }

      let currentSection = 'about';

      sections.forEach((section) => {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop } = element;
          
          if (scrollPosition >= offsetTop) {
            currentSection = section.id;
          }
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.offsetTop - 100;
      window.scrollTo({ 
        top: offsetTop, 
        behavior: 'smooth' 
      });
    }
  };

  return (
    <div className="fixed right-8 top-1/2 transform -translate-y-1/2 z-40 hidden lg:block">
      <div className="flex flex-col items-end gap-6">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => scrollToSection(section.id)}
            className="group flex items-center gap-3"
            aria-label={`Go to ${section.label}`}
          >
            <span
              className={`text-sm font-medium transition-all duration-300 ${
                activeSection === section.id
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 translate-x-4 group-hover:opacity-100 group-hover:translate-x-0'
              } ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
            >
              {section.label}
            </span>
            <div
              className={`w-3 h-3 rounded-full border-2 transition-all duration-300 ${
                activeSection === section.id
                  ? 'bg-cyan-500 border-cyan-500 scale-125'
                  : isDark 
                    ? 'border-gray-600 hover:border-cyan-500' 
                    : 'border-gray-400 hover:border-cyan-500'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default SideNavIndicator;